<script setup lang="ts">
import { computed, useSlots } from 'vue';

interface ImgProductCardLayoutProps {
  enableHoverColor: boolean;
}

const props = defineProps<ImgProductCardLayoutProps>();

const slots = useSlots();
const hasHeader = computed(() => !!slots['header']);
</script>

<template>
  <div class="img-product-card-layout">
    <slot name="header" />
    <div
      class="product-content"
      :class="{
        hovering: props.enableHoverColor,
        'has-header': hasHeader,
      }"
    >
      <div class="image">
        <slot name="image" />
      </div>
      <div class="content" :class="{ 'has-header': hasHeader }">
        <slot name="content" />
      </div>
      <div class="footer">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.img-product-card-layout {
  --min-height-of-image-desktop: 300px;

  border-radius: var(--border-radius-12);
  border: 1px solid var(--color-secondary-93);
  overflow: hidden;

  .product-content {
    width: 100%;
    background: var(--color-white);

    &.hovering {
      @include on-hover {
        background: var(--color-secondary-95);
        transition: background var(--transition-short);
        cursor: pointer;
      }
    }

    display: grid;
    grid-template-rows: minmax(var(--min-height-of-image-desktop), auto);
    grid-template-columns: 1fr 1fr;

    .image {
      grid-column: 1/2;
      width: 100%;
      cursor: pointer;

      overflow: hidden;
      // NOTE: Next two rules are forcing image to not be highier
      // than .content column
      height: 0;
      min-height: 100%;
    }

    .content {
      grid-column: 2/3;
    }

    .footer {
      width: 100%;
      height: 100%;
      display: none;

      grid-column: 3/4;
      border-left: 1px solid var(--color-secondary-93);

      @include for-small-devices-max {
        display: inline-block;
      }
    }
  }

  .product-content.has-header {
    border-top: 1px solid var(--color-secondary-93);
  }

  @include for-large-devices-min {
    .content {
      min-height: 220px;
    }
  }
  @include for-small-devices-max {
    .product-content {
      grid-template-rows: repeat(4, auto);
      // FYI: should be auto, but firefox mobile on android have issues with it
      grid-template-columns: 1fr;

      .image {
        grid-column: 1/4;
        width: 100%;
        height: auto;
        aspect-ratio: 16/9;
        overflow: hidden;
      }

      .content {
        grid-column: 1/4;
        border-bottom: unset;
        border-bottom-left-radius: unset;
      }

      .footer {
        grid-column: 1/4;
        border: unset;
        border-bottom-left-radius: var(--border-radius-12);
        border-top-right-radius: unset;
      }
    }
  }
}
</style>
